import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './MediaDropStep.module.scss';
import { MediaDropKind, MEDIA_KIND_TYPE_NAME, detectDroppedMediaKind } from './mediaDropHelpers';

export interface MediaDropStepProps {
  /** Type imposé par l'appelant (MediaSelector filtré) : tout autre fichier est refusé. */
  restrictTo? : MediaDropKind;
  onFile      : (file: File, kind: MediaDropKind) => void;
  /** Une diapositive se compose d'une image ET d'un son : il n'y a pas de fichier à déposer. */
  onSlideshow : () => void;
  onCancel    : () => void;
}

const KIND_NOUN: Record<MediaDropKind, string> = {
  video: 'une vidéo',
  audio: 'un son',
  image: 'une image',
};

export const MediaDropStep = ({ restrictTo, onFile, onSlideshow, onCancel }: MediaDropStepProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [message, setMessage]   = useState('');
  const [showOthers, setShowOthers] = useState(false);

  const accept = restrictTo ? `${restrictTo}/*` : 'video/*,audio/*,image/*';

  const take = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const kind = detectDroppedMediaKind(file);

    if (kind === null) {
      setMessage(`« ${file.name} » n'est ni une vidéo, ni un son, ni une image.`);
      return;
    }
    if (restrictTo && kind !== restrictTo) {
      setMessage(`« ${file.name} » n'est pas ${KIND_NOUN[restrictTo]} : ici, seul ce type de média est accepté.`);
      return;
    }
    setMessage(files.length > 1 ? 'Un seul média à la fois : le premier a été retenu.' : '');
    onFile(file, kind);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    take(event.dataTransfer.files);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    take(event.target.files);
    event.target.value = '';
  };

  const title = restrictTo ? `Déposez ${KIND_NOUN[restrictTo]} ici` : 'Déposez votre vidéo ici';
  const hint  = 'Glissez le fichier dans ce cadre, ou choisissez-le sur votre ordinateur.';

  return (
    <div className={classNames(styles.root)}>
      <h1 className={classNames(styles.heading)}>{restrictTo && restrictTo !== 'video' ? 'Nouveau média' : 'Nouvelle vidéo'}</h1>

      <div className={classNames(styles.zone, dragging && styles.zone_dragging)}
           onDragOver  = {(event) => { event.preventDefault(); setDragging(true); }}
           onDragLeave = {() => setDragging(false)}
           onDrop      = {handleDrop}>
        <svg className={classNames(styles.zone_icon)} viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 16V4" /><path d="m7 9 5-5 5 5" /><path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
        </svg>
        <span className={classNames(styles.zone_title)}>{title}</span>
        <span className={classNames(styles.zone_hint)}>{hint}</span>
        <button type="button" className={classNames(styles.pick_button)} onClick={() => inputRef.current?.click()}>
          Choisir un fichier
        </button>
        <input ref={inputRef} type="file" accept={accept} className={classNames(styles.hidden_input)}
               onChange={handleInputChange} tabIndex={-1} aria-hidden="true" />
      </div>

      {message !== '' && <p className={classNames(styles.message)} role="alert">{message}</p>}

      <div className={classNames(styles.footer)}>
        {!restrictTo && !showOthers && (
          <button type="button" className={classNames(styles.link_button)} onClick={() => setShowOthers(true)}>
            Ajouter plutôt un son, une image ou une diapositive
          </button>
        )}
        {!restrictTo && showOthers && (
          <div className={classNames(styles.others)}>
            <span>Un son ou une image se déposent dans le même cadre.</span>
            <button type="button" className={classNames(styles.link_button)} onClick={onSlideshow}>
              Créer une diapositive (une image avec un son)
            </button>
          </div>
        )}
        <button type="button" className={classNames(styles.link_button)} onClick={onCancel}>Annuler</button>
      </div>
    </div>
  );
};

export { MEDIA_KIND_TYPE_NAME };
export default MediaDropStep;
